import {Button, Paper} from '@material-ui/core';
import {Lens} from 'monocle-ts';
import React, {useEffect} from 'react';
import Loading from 'Shared/components/Loading';
import RequiredTextInput from 'Shared/components/RequiredTextInput';
import withSelectedAgency, {WithSelectedAgencyProps} from 'Shared/components/withSelectedAgency';
import {changeWithLens} from 'Shared/utilities/changeWithLens';
import {ValidationError} from 'Users/ducks/independentAgent';
import {changeUserError, saveNameInformation} from 'Users/ducks/userAccount';
import {
  eqNameInformation,
  InvalidNameInformation,
  NameInformation,
  SavingNameInformation,
  UnchangedNameInformation,
  ValidNameInformation,
} from 'Users/models/NameInformation';
import {validateName} from '../AgentAdministration/AgencyAdministratorAgentEntry/IndependentAgentEntry';

type UserInformationSectionProps = {
  userId: string;
  nameInformation: NameInformation;
  currentNameInformation: NameInformation;
  onChange: (nameInfo: NameInformation) => void;
  saveNameInformation: typeof saveNameInformation;
  errors: ValidationError[];
  onChangeUserError: typeof changeUserError;
} & WithSelectedAgencyProps;

const NameInformationLens = Lens.fromProp<NameInformation>();
const UserInformationSection = (props: UserInformationSectionProps) => {
  const {selectedAgency} = props;
  useEffect(() => {
    props.onChange(props.currentNameInformation);
  }, []);
  const isEditing = !(props.nameInformation instanceof UnchangedNameInformation);
  const hasNameInformationChanged = eqNameInformation.equals(props.nameInformation, props.currentNameInformation);
  const changeValue = changeWithLens(NameInformationLens, props.onChange, props.nameInformation);

  const userInfoSection = (
    <Paper className="user-profile__user-info-section">
      <div className="user-info-section__header">
        <h3>User Information</h3>
      </div>
      <div className="user-info-section__name-fields">
        <RequiredTextInput
          className="user-info-section__profile-input"
          label="First Name"
          disabled={!isEditing}
          onChange={changeValue('firstName')}
          value={props.nameInformation.firstName}
          additionalErrors={props.errors}
          onValidate={validateName}
          changeError={props.onChangeUserError}
        />
        <RequiredTextInput
          className="user-info-section__profile-input"
          label="Last Name"
          disabled={!isEditing}
          onChange={changeValue('lastName')}
          value={props.nameInformation.lastName}
          additionalErrors={props.errors}
          onValidate={validateName}
          changeError={props.onChangeUserError}
        />
      </div>
      <div className="user-info-section__actions-container">
        {isEditing ? (
          <>
            <Button
              variant="contained"
              className="user-info-section__cancel-user-information-button"
              onClick={() => {
                props.onChange(props.currentNameInformation);
                props.onChangeUserError([]);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={hasNameInformationChanged || props.errors.length > 0}
              color="primary"
              variant="contained"
              className="user-info-section__save-user-information-button"
              onClick={() =>
                props.saveNameInformation(
                  props.userId,
                  props.nameInformation as ValidNameInformation,
                  selectedAgency?.encryptedIdentifier
                )
              }
            >
              Save
            </Button>
          </>
        ) : (
          <Button
            className="user-info-section__edit-button"
            color="primary"
            variant="contained"
            onClick={() =>
              props.onChange(
                new InvalidNameInformation(props.nameInformation.firstName, props.nameInformation.lastName)
              )
            }
          >
            Edit&nbsp;&nbsp;
            <i className="fa fa-edit" />
          </Button>
        )}
      </div>
    </Paper>
  );

  return props.nameInformation instanceof SavingNameInformation ? (
    <Paper className="user-profile__user-info-section">
      <Loading />
    </Paper>
  ) : (
    userInfoSection
  );
};

export default withSelectedAgency(UserInformationSection);
